<script lang="ts" setup>
import { useLocalStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { computed, onMounted, ref } from 'vue';

import { setHelpfulReview } from '@/api/reviews';
import ThumbsUpIcon from '@/components/base/assets/ThumbsUpIcon.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import RatingStars from '@/components/pdp/reviews/RatingStars.vue';
import { useCallback } from '@/composables/useCallback';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { ApprovedReview } from '@/lib/reviews';
import mascots from '@/utils/mascots';
import { getUpdatedUnitName } from '@/utils/product';
import states from '@/utils/states';

dayjs.extend(relativeTime);

const props = defineProps<{
  featured?: boolean;
  review: ApprovedReview;
}>();

const storage = useLocalStorage<number[]>('helpfulButton', []);

const formatDate = (date: string) => {
  const dateObj = dayjs(date);
  const diffDays = dayjs().diff(dateObj, 'days');

  if (diffDays > 7 && diffDays < 28) {
    if (diffDays < 14) {
      return `1 week ago`;
    }
    return `${dayjs().diff(dateObj, 'weeks')} weeks ago`;
  }

  if (diffDays < 84) {
    return dateObj.fromNow();
  }

  return date;
};

const isReadMoreActive = ref(false);
const helpfulReviewIds = ref<number[]>([]);
const isHelpfulSelected = computed(() => helpfulReviewIds.value.includes(props.review.id));

const retrieveCityLocation = computed(() => {
  const { locality } = props.review;
  const city = locality ? locality.split(', ')[0] : '';
  return city;
});

const retrieveStateLocation = computed(() => {
  const { locality } = props.review;
  if (!locality) return '';
  const stateAbbreviation = locality.split(', ')[1];
  const stateNameFound = states[stateAbbreviation];
  return stateNameFound ?? locality;
});

const updateLocalStorage = (isHelpful: boolean) => {
  const currentIds = storage.value;

  if (isHelpful) helpfulReviewIds.value = [...currentIds, props.review.id];
  else helpfulReviewIds.value = currentIds.filter((id) => id !== props.review.id);

  storage.value = helpfulReviewIds.value;
};

const currentScore = computed(() => {
  let score = props.review.helpfulScore || 1;

  score -= 1;
  if (isHelpfulSelected.value) score += 1;

  return score;
});

const setHelpfulCallback = useCallback(async () => {
  const isHelpful = !isHelpfulSelected.value;
  updateLocalStorage(isHelpful);
  await setHelpfulReview(props.review.id, isHelpful);
});

const { flags } = useFeatureFlags();

const formattedUnitName = computed(() =>
  getUpdatedUnitName({ en: props.review.size }, flags.testVariantNameDisplay),
);

onMounted(() => {
  helpfulReviewIds.value = storage.value ?? [];
});
</script>

<template>
  <div>
    <div class="relative flex flex-col justify-between lg:flex-row">
      <div class="flex flex-col max-w-4xl">
        <div class="flex items-center">
          <RatingStars :rating="review.rating ?? 0" />
          <SmallBodyText class="ml-1">&ndash; {{ formatDate(review.date) }}</SmallBodyText>
        </div>
        <div class="flex items-center mt-3">
          <div aria-hidden="true" class="relative">
            <img
              alt="character icon"
              class="w-10 h-10 lg:w-14 lg:h-14"
              fetchpriority="low"
              :src="mascots[review.id % mascots.length]"
            />
            <img
              alt="check verified icon"
              class="absolute w-4 left-7 top-6 lg:w-6 lg:ml-3 lg:top-8"
              src="@/assets/pdp/review/check-circle.png"
            />
          </div>
          <div class="ml-4">
            <LargeBodyText class="capitalize">
              <span>{{ review.author }}</span>
              <span v-if="review.locality"
                >, {{ retrieveCityLocation }}, {{ retrieveStateLocation }}</span
              >
            </LargeBodyText>
            <BaseBodyText class="font-semibold text-nuts-lime-800">Verified purchaser</BaseBodyText>
          </div>
        </div>
        <div v-if="review.content.length > 260" class="flex flex-col items-start mt-6">
          <div :class="[{ 'line-clamp-2 lg:h-full h-10': !isReadMoreActive }]">
            <BaseBodyText class="mb-2 text-neutral-600"> {{ review.content }} </BaseBodyText>
          </div>
          <button
            class="p-0 mt-1 text-sm text-black underline bg-transparent border-0 appearance-none cursor-pointer sm:mt-0 font-proxima-nova md:text-base"
            data-test="read-more-button"
            data-promo="1"
            data-promo-name="Reviews"
            data-promo-creative="Listing: More"
            type="button"
            @click.prevent="isReadMoreActive = !isReadMoreActive"
          >
            Read {{ isReadMoreActive ? 'less' : 'more' }}
          </button>
        </div>
        <BaseBodyText v-else class="mt-6 text-neutral-600"> {{ review.content }} </BaseBodyText>
      </div>
      <div class="flex flex-col justify-between shrink-0">
        <div class="flex flex-col items-end mt-4 lg:mt-0">
          <SmallBodyText
            v-if="featured"
            class="absolute top-0 right-0 px-3 py-1 font-semibold rounded-full lg:px-4 w-fit bg-nuts-red-200 lg:static"
            data-test="featured-review"
          >
            Featured Review
          </SmallBodyText>
          <div class="flex flex-row items-center w-full lg:flex-col">
            <SmallBodyText v-if="currentScore" :class="{ 'mt-2': featured }">
              {{ currentScore }} {{ currentScore > 1 ? 'people' : 'person' }} found this review
              helpful
            </SmallBodyText>
            <button
              :aria-label="isHelpfulSelected ? 'Remove helpful vote' : 'Helpful'"
              class="flex items-center px-6 ml-auto transition-colors border h-9 duration-400 hover:shadow-sharp md:cursor-pointer disabled:cursor-not-allowed rounded-4xl"
              :class="{
                'lg:mt-3': currentScore || featured,
                'text-white bg-black hover:bg-nuts-neutral-700': isHelpfulSelected,
                'border-solid hover:border-transparent border-neutral-500 text-black bg-white hover:bg-neutral-100':
                  !isHelpfulSelected,
              }"
              :disabled="setHelpfulCallback.isPending"
              @click="setHelpfulCallback.execute"
            >
              <ThumbsUpIcon
                class="mr-1"
                :class="isHelpfulSelected ? 'text-white' : 'text-black'"
                :size="16"
              />
              <BaseBodyText>Helpful</BaseBodyText>
            </button>
          </div>
        </div>
        <div v-if="review.size" class="mt-4 text-right lg:mt-0">
          <SmallBodyText>Size: {{ formattedUnitName }}</SmallBodyText>
        </div>
      </div>
    </div>
  </div>
</template>
